/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {GenericObject} from '@ion/core/src/types/entities';
import {createListenerRegistry} from '@ion/core/src/utils/ListenerRegistry';
import JobStatusObserver from '@ion/services/src/websocket/JobStatusObserver';
import {JobStatusUpdate, ObservableEntityId} from '@ion/services/src/websocket/types';
import {onSocketReconnected} from '@/store/actions/websocket';
import {liveStatusActive} from './signals/liveUpdates';
import {diffSets} from './JobSearchEngineUtils';
import {getJobIds, subscribe as onWatchListChanged} from './WatchedJobs';

export type JobStatusListener = (update: JobStatusUpdate, jobId: number) => void;

const listeners = createListenerRegistry<[JobStatusUpdate, number]>();

let observed = new Set<number>();

let disposeReconnect: (() => void) | null = null;
let disposeWatchList: (() => void) | null = null;

let onReconnected: (() => void) | null = null;

/**
 * The single `jobStatus` observer for the whole application.
 *
 *  `Observers.addObserver` appends with no dedupe, so this module must
 * guarantee exactly one observer identity for the whole app - independently of
 * `JobSubscriptions`, which guarantees the same for `jobProgress`. Hence one
 * module-level const rather than a per-call closure, and hence the diff in
 * `syncObserved` before anything is added. Registering it twice for an id makes
 * every terminal event fire - and therefore *write* - twice.
 */
const onStatus = (change: GenericObject, id?: ObservableEntityId): void => {
  const jobId = Number(id);

  if (observed.has(jobId)) {
    listeners.notify(change as JobStatusUpdate, jobId);
  }
};

/**
 * Reconciles the observed ids with the watch list, which *is* the desired set -
 * there is one consumer, so there is no union to compute.
 *
 * There is no confirmation gate and no held-back departure, unlike
 * `JobSubscriptions`: that machinery exists because paging fires a subscribe
 * and an unsubscribe in the same tick, faster than the round trip, whereas a
 * watch toggle is one id at a time at human speed. Nothing here waits on a
 * reply either - if a subscribe fails, the catch-up query is the backstop.
 */
const syncObserved = (): void => {
  const desired = new Set<number>(liveStatusActive.value ? getJobIds() : []);

  const {added, removed} = diffSets(desired, observed);

  observed = desired;
  if (added.length) {
    JobStatusObserver.addObserver(added, onStatus);
  }
  if (removed.length) {
    JobStatusObserver.removeObserver(removed, onStatus);
  }
};

/**
 * Subscribes every watched job for `jobStatus`, and keeps that in step with the
 * watch list for the rest of the session.
 *
 * Call this once per sign-in from `initialization.ts`, after the watch list has
 * been hydrated - never from a component, for the reason `websocket.ts` gives
 * for the socket bootstrap itself.
 *
 * It does not wait for a connection and must not try to:
 * `EntityObserver.syncSubscriptions` replays every entity's observed ids once
 * the socket opens, so subscribing before it connects is safe.
 *
 * **The reconnect action is the catch-up query alone, not a re-subscribe.**
 * `EntityObserver.onReconnection` resynchronises every observed id *before* it
 * notifies these listeners, so re-adding the observer here would register a
 * second identity for every watched id and double every terminal event.
 *
 * There is no unsubscribe on terminal: a terminal job stays watched and stays
 * subscribed. The subscription is dropped only by an explicit unwatch.
 *
 * ```typescript
 *
 *   import * as JobStatusSubscriptions from './JobStatusSubscriptions';
 *
 *   JobStatusSubscriptions.start(runCatchUp);
 *
 * ```
 *
 * @param onReconnect Invoked after the socket reconnects, when terminal events
 *        emitted during the outage have been lost.
 */
export const start = (onReconnect?: () => void): void => {
  onReconnected = onReconnect ?? null;
  if (!disposeWatchList) {
    disposeWatchList = onWatchListChanged(syncObserved);
  }
  if (!disposeReconnect) {
    disposeReconnect = onSocketReconnected(function() {
      onReconnected?.();
    });
  }
  syncObserved();
};

/**
 * Unsubscribes every watched job and releases the session's listeners, leaving
 * the observers of every other entity - the `jobProgress` channel included -
 * intact.
 */
export const stop = (): void => {
  disposeWatchList?.();
  disposeReconnect?.();
  disposeWatchList = null;
  disposeReconnect = null;
  onReconnected    = null;
  observed         = new Set<number>();
  JobStatusObserver.removeAllObservers();
};

/**
 * Registers a listener invoked with every terminal event for a watched job, and
 * returns its disposer.
 *
 * Receiving the event *is* the terminal signal - the service emits `jobStatus`
 * only once a job is terminal - so there is no status to evaluate here.
 *
 * @param   listener Invoked with the change and the numeric job id.
 * @returns A function which removes the listener when invoked.
 */
export const onTerminal = (listener: JobStatusListener): (() => void) => (
  listeners.subscribe(listener)
);

/**
 * Clears the module. Module state outlives a test case, so this exists for test
 * isolation - never call it from application code.
 */
export const reset = (): void => {
  stop();
  listeners.clear();
};
